import { useState } from 'react';

import { IconChevron, IconProblem, IconSuggestion } from '../../../common/icons';
import { formatDateTime } from '../../../core/utils/date';
import { Gate } from '../../../global/contexts/auth/auth';
import ClassifyTicketForm from '../forms/classify';
import ResolveTicketForm from '../forms/resolve';
import { affectedOf, classificationOf, createdAtOf, isClassified, kindOf, reporterOf, stateOf } from '../utils/tickets';

// Una fila de la ventana, y su despliegue.
//
// DESPLEGAR NO PIDE NADA. El listado corre `SELECT k.*` sobre la misma vista
// que el detalle, así que la fila ya llegó con el texto del reporte, la nota de
// clasificación y la de resolución. Abrirla es mostrar lo que ya está en la
// mano. Por eso no hay un spinner acá, ni un `useTicket(id)`: pedir de nuevo lo
// que ya se tiene es latencia inventada.
//
// LO QUE SE VE CERRADO ES LO QUE SIRVE PARA BARRER LA COLA —tipo, título,
// proyecto, quién reportó, cuándo, en qué estado— y lo que se ve abierto es lo
// que hace falta para DECIDIR. El cuerpo del reporte es lo primero de adentro
// porque es lo único que nadie puede resumir por quien lo escribió.

const KIND_ICON = {
    problema: IconProblem,
    sugerencia: IconSuggestion,
};

export default function TicketRow({ ticket }) {
    const [open, setOpen] = useState(false);

    const kind = kindOf(ticket.kind);
    const state = stateOf(ticket.state);
    const reporter = reporterOf(ticket);
    const affected = affectedOf(ticket);
    const created = createdAtOf(ticket);

    // Un tipo que este panel no conoce todavía no deja la celda vacía: muestra
    // el valor crudo, sin icono.
    const KindIcon = KIND_ICON[ticket.kind] ?? null;

    return (
        <li className={'ticket' + (open ? ' ticket--open' : '')}>
            <div className="ticket__row">
                <button
                    type="button"
                    className="ticket__toggle"
                    onClick={() => setOpen(value => !value)}
                    aria-expanded={open}
                    aria-label={open ? 'Cerrar el ticket' : 'Abrir el ticket'}
                >
                    <IconChevron />
                </button>

                {/* Los dos tipos comparten tinta a propósito: el tipo es un dato
                    y no un veredicto. Lo que cambia es la silueta. */}
                <span className="ticket__kind" title={kind?.label ?? ticket.kind}>
                    {KindIcon ? <KindIcon /> : null}
                    <span className="ticket__kind-label">{kind?.label ?? ticket.kind}</span>
                </span>

                <span className="ticket__identity">
                    <span className="ticket__title">{ticket.title}</span>
                    <span className="ticket__id">#{ticket.id}</span>
                </span>

                <span className="ticket__project">
                    {ticket.project_name ?? <span className="ticket__none">sin proyecto</span>}
                </span>

                <span className="ticket__reporter">
                    {reporter
                        ? (
                            <>
                                {reporter.name}
                                {/* Quien reportó puede no ser una persona de
                                    Mastermind: `reporter_ref` es a quién
                                    representa quien lo cargó. Decirlo evita que
                                    un nombre suelto se lea como una cuenta. */}
                                {!reporter.known && <span className="ticket__external"> (externo)</span>}
                            </>
                        )
                        : <span className="ticket__none">sin quién reportó</span>}
                </span>

                <span className="ticket__when">{created ? formatDateTime(created) : '—'}</span>

                <span className={'ticket__state ticket__state--' + (state?.tone ?? 'idle')}>
                    {state?.label ?? ticket.state}
                </span>
            </div>

            {open && <TicketBody ticket={ticket} affected={affected} />}
        </li>
    );
}

function TicketBody({ ticket, affected }) {
    const classification = classificationOf(ticket.classification);

    return (
        <div className="ticket__body">
            {/* Tal como lo reportaron. `white-space: pre-wrap` en el CSS: los
                saltos de línea de quien escribió son parte de lo que dijo. */}
            <p className="ticket__report">{ticket.body}</p>

            <dl className="ticket__facts">
                <div className="ticket__fact">
                    <dt>Entorno</dt>
                    <dd>{affected.environment ?? <span className="ticket__none">no se sabe</span>}</dd>
                </div>
                <div className="ticket__fact">
                    <dt>Versión</dt>
                    <dd>
                        {affected.version
                            ? <code className="ticket__version">{affected.version}</code>
                            : <span className="ticket__none">no se sabe</span>}
                    </dd>
                </div>
                <div className="ticket__fact">
                    <dt>Cargado por</dt>
                    <dd>{ticket.recorded_by_name ?? '—'}</dd>
                </div>
            </dl>

            {/* NI EL ENTORNO NI LA VERSIÓN SE COMPLETAN SOLOS. Un ticket sin
                versión no toma la desplegada del entorno: eso sería afirmar qué
                versión tenía quien reportó. Cuando falta, falta. */}
            {(!affected.environment || !affected.version) && (
                <p className="ticket__gap">
                    Lo que no se supo al recibirlo queda vacío. No se completa con lo que
                    tenga el entorno hoy, porque eso sería afirmar qué versión estaba usando
                    quien reportó.
                </p>
            )}

            <section className="ticket__step">
                <h4 className="ticket__step-title">Clasificación</h4>

                {isClassified(ticket) ? (
                    <div className="ticket__verdict">
                        <span className={'ticket__label ticket__label--' + (classification?.tone ?? 'idle')}>
                            {classification?.label ?? ticket.classification}
                        </span>
                        <span className="ticket__byline">
                            {ticket.classified_by_name ?? 'alguien'}
                            {ticket.classified_at && ' · ' + formatDateTime(ticket.classified_at)}
                        </span>
                        {ticket.classification_note && (
                            <p className="ticket__step-note">{ticket.classification_note}</p>
                        )}
                        {ticket.plan_id && (
                            <p className="ticket__step-note">
                                Relacionado con el plan #{ticket.plan_id}.
                            </p>
                        )}
                    </div>
                ) : (
                    <p className="ticket__pending">
                        Nadie lo miró todavía. Recibirlo no confirmó un error ni aprobó una
                        sugerencia.
                    </p>
                )}

                {/* Reclasificar es legítimo y deja autor y fecha nuevos, con el
                    histórico en los eventos — así que el formulario sigue
                    disponible después del primer veredicto. */}
                <Gate domain="tasks:write">
                    <ClassifyTicketForm ticket={ticket} />
                </Gate>
            </section>

            <section className="ticket__step">
                <h4 className="ticket__step-title">Resolución</h4>

                {ticket.state === 'resuelto' ? (
                    <div className="ticket__verdict">
                        <span className="ticket__byline">
                            {ticket.resolved_by_name ?? 'alguien'}
                            {ticket.resolved_at && ' · ' + formatDateTime(ticket.resolved_at)}
                        </span>
                        {ticket.resolution_note && (
                            <p className="ticket__step-note">{ticket.resolution_note}</p>
                        )}
                    </div>
                ) : (
                    <>
                        <p className="ticket__pending">
                            Sigue abierto para quien lo reportó. Resolver un incidente no cierra
                            sus tickets: la solución puede no estar disponible para todos los
                            entornos y usuarios a la vez, así que cada uno se cierra aparte y
                            diciendo qué se le resolvió.
                        </p>
                        <Gate domain="tasks:write">
                            <ResolveTicketForm ticket={ticket} />
                        </Gate>
                    </>
                )}
            </section>
        </div>
    );
}
